import { BodyText, Button, FlexColumn, FlexRow, SvgIcon } from '@make-software/cspr-design';
import React, { useEffect, useState } from 'react';
import {
  ErrorText,
  ErrorTextContainer,
  StyledInput,
  StyledTooltip
} from 'app/components/common/common-styles';
import styled from 'styled-components';
import {
  AppCategory,
  AppCategoryRequest,
  createAppCategory,
  updateAppCategory
} from 'app/console-api/apps/categories';
import { StyledActionIcon } from 'app/components/common/table/styled-elements';
import { AppEnvs } from 'config/env';
import InfoIcon from 'assets/icons/ic-info.svg';

const StyledAddElement = styled(BodyText)<{ isPointer?: boolean }>(({ theme, isPointer }) =>
  theme.withMedia({
    cursor: isPointer ? 'pointer' : 'default',
    fontWeight: '400',
    lineHeight: '24px',
    marginBottom: '12px',
    '&:hover': {
      color: isPointer
        ? theme.styleguideColors.fillPrimaryBlueClick
        : theme.styleguideColors.contentTertiary
    }
  })
);

const NAME_REGEX = /^[A-Za-z_1-9]+$/;

interface CategoriesTypeControlProps {
  appID: string;
  categories: AppCategory[];
}

interface CategoryWithState {
  id?: number;
  name: string;
  isNew: boolean;
  isEdited: boolean;
  draftName: string;
}

export const AppCategoriesTypeControl: React.FC<CategoriesTypeControlProps> = ({
  appID,
  categories
}) => {
  const [categoriesWithState, setCategoriesWithState] = useState<CategoryWithState[]>([]);
  const [errors, setErrors] = useState(new Map<number, string>());

  useEffect(() => {
    const initialCategories = categories.map((category) => ({
      id: category.id,
      name: category.name,
      isNew: false,
      isEdited: false,
      draftName: category.name
    }));
    setCategoriesWithState(initialCategories);
  }, [categories]);

  const validateOne = (idx: number, draftName: string): boolean => {
    const updatedErrors = new Map(errors);
    if (!draftName) {
      updatedErrors.set(idx, 'Category name is required');
      setErrors(updatedErrors);
      return false;
    }
    if (!NAME_REGEX.test(draftName)) {
      updatedErrors.set(idx, 'Only letters, numbers and underscore (_) are allowed.');
      setErrors(updatedErrors);
      return false;
    }
    if (categoriesWithState.filter((row) => row.draftName === draftName).length > 1) {
      updatedErrors.set(idx, 'Category name must be unique');
      setErrors(updatedErrors);
      return false;
    }

    updatedErrors.delete(idx);
    setErrors(updatedErrors);
    return true;
  };

  const onEdit = (index: number) => {
    const updatedCategories = [...categoriesWithState];
    const category = updatedCategories[index];
    category.isEdited = true;
    setCategoriesWithState(updatedCategories);
  };

  const onCancelEdit = (index: number) => {
    const updatedCategories = [...categoriesWithState];
    const category = updatedCategories[index];
    category.isEdited = false;
    category.draftName = category.name;
    validateOne(index, category.draftName);
    setCategoriesWithState(updatedCategories);
  };

  const onSaveEdit = (index: number) => {
    const updatedCategories = [...categoriesWithState];
    const category = updatedCategories[index];
    if (!validateOne(index, category.draftName)) {
      return;
    }
    category.name = category.draftName;
    updateAppCategory(appID, category.id || 0, { name: category.name })
      .then((response) => {
        category.isEdited = false;
        setCategoriesWithState(updatedCategories);
      })
      .catch((error: any) => {
        console.error('Failed to update category', error);
      });
  };

  const addNew = () => {
    if (categoriesWithState.length >= AppEnvs.appCategoryLimit) return;
    const newCategory = {
      id: 0,
      name: '',
      isNew: true,
      isEdited: false,
      draftName: ''
    };
    const updatedCategories = [...categoriesWithState, newCategory];
    setCategoriesWithState(updatedCategories);
  };

  const onCancelNew = (index: number) => {
    const updatedCategories = [...categoriesWithState];
    updatedCategories.splice(index, 1);
    setCategoriesWithState(updatedCategories);
    const updatedErrors = new Map(errors);
    updatedErrors.delete(index);
    setErrors(updatedErrors);
  };

  const onSaveNew = (index: number) => {
    const updatedCategories = [...categoriesWithState];
    const newCategory = updatedCategories[index];
    const request: AppCategoryRequest = { name: newCategory.draftName };
    if (!validateOne(index, newCategory.draftName)) {
      return;
    }
    createAppCategory(appID, request)
      .then((response) => {
        newCategory.isNew = false;
        newCategory.isEdited = false;
        newCategory.id = response.data.id;
        newCategory.name = response.data.name;
        updatedCategories[index] = newCategory;
        setCategoriesWithState(updatedCategories);
      })
      .catch((error: any) => {
        console.error('Failed to create category', error);
      });
  };

  const onNameChange = (index: number, value: string) => {
    const updatedCategories = [...categoriesWithState];
    const category = updatedCategories[index];
    category.draftName = value;
    if (category.isNew || category.isEdited) {
      setCategoriesWithState(updatedCategories);
    }
  };

  return (
    <>
      <FlexRow gap={4} align={'center'}>
        <BodyText size={1}>App categories:</BodyText>
        <StyledTooltip
          tooltipContent={
            <BodyText size={3} scale={'sm'}>
              Categories will be assigned to tasks on the Application. Try to keep name simple as
              you need this data to integrate into your code.
            </BodyText>
          }
        >
          <SvgIcon src={InfoIcon} color={'gray'} />
        </StyledTooltip>
      </FlexRow>

      <FlexColumn gap={8}>
        {categoriesWithState.map((one, index) => (
          <>
            <FlexRow key={index} justify={'center'} align={'center'} gap={5}>
              <StyledInput
                label={index === 0 ? <BodyText size={1}>Category key</BodyText> : ''}
                placeholder="category_name"
                disabled={!(one.isEdited || one.isNew)}
                height={'36'}
                value={one.draftName}
                onChange={(event) => onNameChange(index, event.target.value)}
              />
              {index !== 0 && !one.isNew && !one.isEdited && (
                <Button
                  color={'utility'}
                  height={'36'}
                  width={'100'}
                  onClick={(event) => onEdit(index)}
                >
                  <StyledActionIcon icon="ri:pencil-fill" />
                </Button>
              )}
              {one.isEdited && (
                <FlexRow gap={5}>
                  <Button
                    color={'utility'}
                    height={'36'}
                    width={'100'}
                    onClick={(event) => onCancelEdit(index)}
                  >
                    Cancel
                  </Button>
                  <Button
                    color={'primaryBlue'}
                    height={'36'}
                    width={'100'}
                    onClick={(event) => onSaveEdit(index)}
                  >
                    Save
                  </Button>
                </FlexRow>
              )}
              {one.isNew && (
                <FlexRow gap={5}>
                  <Button
                    color={'utility'}
                    height={'36'}
                    width={'100'}
                    onClick={(event) => onCancelNew(index)}
                  >
                    Cancel
                  </Button>
                  <Button
                    color={'primaryBlue'}
                    height={'36'}
                    width={'100'}
                    onClick={(event) => onSaveNew(index)}
                  >
                    Save
                  </Button>
                </FlexRow>
              )}
            </FlexRow>
            {errors.has(index) && (
              <ErrorTextContainer justify={'flex-start'}>
                <ErrorText size={3}>{errors.get(index)}</ErrorText>
              </ErrorTextContainer>
            )}
          </>
        ))}
        <StyledAddElement
          size={3}
          scale={'md'}
          variation={categoriesWithState.length >= AppEnvs.appCategoryLimit ? 'gray' : 'blue'}
          isPointer={categoriesWithState.length < AppEnvs.appCategoryLimit}
        >
          <div onClick={addNew}> + Add Category</div>
        </StyledAddElement>
      </FlexColumn>
    </>
  );
};
